// src/data/workingCopy.js - RULING 305.1 item 2 (Bob 2026-10-11, "AGREED"): "Officers work on amounts, rules and the claim
// form in a private working copy, then press 'Publish to the claim' when it's finished. Only then does the member see it."
// Built as ruling 305.10 (by delegation, Record 647). PURE: no Firestore, no clock (the caller passes the time).
//
// THE COPY. claims/{claimId}/workingCopies/{officerUid} - one per officer, his alone (firestore.rules: only he reads or
// writes it; the member never does). It holds two things and nothing else:
//   completion  the claim-form blanks and the amount, as he typed them (claimCompletion.js COMPLETION_FIELDS)
//   ruleActs    rules he adds or sets aside, in order ({ verb: 'add'|'set_aside', ruleId, ruleLabel, note, at })
// THE PUBLISH. publishPlan() replays both onto the claim AS IT STANDS AT PUBLISH, through the very functions the claim
// page always used (completionPatch, addRule, setAsideRule) - so every check those make is made again, against the
// current claim - and returns ONE patch and the notifications ruling 13 requires. The page writes the patch, the
// notifications and the copy's deletion in one batch: all of it, or none of it.
// NOT IN THE COPY (305.1 item 3): acts that carry dates or deadlines - returning, suspending, closing, recording service
// on the carrier - stay immediate and visible. So do notes (305.7 has its own rule) and documents.
import { completionPatch, validateValue, COMPLETION_FIELDS } from './claimCompletion';
import { addRule, setAsideRule } from './claimRules';

export const MAX_RULE_ACTS = 50;
const s = (v) => (v == null ? '' : String(v)).trim();

export function isOfficer(user) {
  return !!user && (user.isAdmin === true || ['admin', 'local_chair', 'general_chair'].includes(s(user.role)));
}
// Who works in private: an officer, on a claim that has left draft (a draft is its author's alone - ruling 56).
export function worksInPrivate(user, claim) {
  return isOfficer(user) && !!claim && s(claim.status) !== 'draft' && !!s(user.uid);
}

export function emptyCopy(user) {
  return { holderUid: s(user && user.uid), holderRole: s(user && user.role), completion: {}, ruleActs: [] };
}
function norm(wc, user) {
  const c = wc || emptyCopy(user);
  return {
    holderUid: s(c.holderUid) || s(user && user.uid),
    holderRole: s(c.holderRole) || s(user && user.role),
    completion: Object.assign({}, c.completion || {}),
    ruleActs: Array.isArray(c.ruleActs) ? c.ruleActs.slice() : [],
  };
}

// The claim as it would read after the copy is published (for the officer's own preview, and to check a rule act
// against the rules already pending).
export function previewClaim(claim, user, wc, atIso) {
  const p = publishPlan(claim, user, wc, atIso);
  return p.ok ? Object.assign({}, claim, p.patch) : claim;
}

// Add values from the "fill in what's missing" box to the copy. Each is validated as the claim page validates it.
export function withCompletion(wc, user, values) {
  const copy = norm(wc, user);
  const errors = [];
  for (const f of COMPLETION_FIELDS) {
    if (!values || !(f.key in values)) continue;
    const r = validateValue(f.key, values[f.key]);
    if (!r.ok) { errors.push(r.error); continue; }
    copy.completion[f.key] = s(values[f.key]);
  }
  return errors.length ? { ok: false, errors, copy: null } : { ok: true, errors: [], copy };
}

// Add a rule act to the copy. It is checked now against the claim as the copy would leave it, and again at publish.
export function withRuleAct(claim, user, wc, act, atIso) {
  const copy = norm(wc, user);
  const a = act || {};
  const verb = a.verb === 'set_aside' ? 'set_aside' : a.verb === 'add' ? 'add' : '';
  if (!verb) return { ok: false, errors: ['Say whether the rule is added or set aside.'], copy: null };
  if (copy.ruleActs.length >= MAX_RULE_ACTS) return { ok: false, errors: ['Publish what is in your working copy first.'], copy: null };
  const entry = { verb, ruleId: s(a.ruleId), ruleLabel: s(a.ruleLabel), note: s(a.note), at: s(atIso) };
  const trial = norm(copy, user); trial.ruleActs.push(entry);
  const p = publishPlan(claim, user, trial, atIso);
  if (!p.ok) return { ok: false, errors: p.errors, copy: null };
  copy.ruleActs.push(entry);
  return { ok: true, errors: [], copy };
}

// Replay the copy onto the claim. Returns { ok, errors, patch, notifications, changed }.
export function publishPlan(claim, user, wc, atIso) {
  const copy = norm(wc, user);
  const errors = [];
  let patch = {};
  const cp = completionPatch(claim, copy.completion);
  if (!cp.ok) errors.push.apply(errors, cp.errors || []);
  else patch = Object.assign(patch, cp.patch || {});
  let working = Object.assign({}, claim, patch);
  let notifications = [];
  let rulesTouched = false;
  for (const a of copy.ruleActs) {
    const input = { ruleId: a.ruleId, ruleLabel: a.ruleLabel, note: a.note, at: a.at || atIso };
    const r = a.verb === 'add' ? addRule(working, user, input) : setAsideRule(working, user, input);
    if (!r.ok) { errors.push.apply(errors, r.errors); continue; }
    const rules = Array.isArray(working.matchedRules) ? working.matchedRules : [];
    working = Object.assign({}, working, { matchedRules: a.verb === 'add' ? rules.concat([r.rulesPatch]) : r.rulesPatch.matchedRules });
    notifications = notifications.concat(r.notifications || []);
    rulesTouched = true;
  }
  if (rulesTouched) patch.matchedRules = working.matchedRules;
  if (errors.length) return { ok: false, errors, patch: null, notifications: [], changed: false };
  return { ok: true, errors: [], patch, notifications, changed: Object.keys(patch).length > 0 };
}

// What the officer sees in his copy, line by line.
export function pendingLines(claim, wc) {
  const copy = norm(wc, null);
  const out = [];
  for (const f of COMPLETION_FIELDS) {
    if (f.key in copy.completion) out.push(f.label + ': ' + (copy.completion[f.key] || '(blank)'));
  }
  for (const a of copy.ruleActs) {
    out.push((a.verb === 'add' ? 'Add rule ' + (a.ruleLabel || a.ruleId) : 'Set aside rule ' + a.ruleId) + (a.note ? ' - ' + a.note : ''));
  }
  return out;
}
export function isEmptyCopy(wc) {
  return !wc || ((!wc.completion || Object.keys(wc.completion).length === 0) && (!Array.isArray(wc.ruleActs) || wc.ruleActs.length === 0));
}

export const PRIVATE_NOTICE = 'You are working in private. The member and the other officers do not see these changes until '
  + 'you press "Publish to the claim". Returning, suspending, closing and recording service still happen at once.';
